import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, Settings, LogOut, AlertCircle, Gamepad2, Keyboard, Sparkles, Camera, Bug } from 'lucide-react';
import { useTheme } from '../styles/ThemeContext';
import { Tooltip } from '../shared/Tooltip';
interface InteriorTopBarProps {
  onReset: () => void;
  onSettings: () => void;
  onControls: () => void;
  onNavigate?: () => void;
  onAssistant?: () => void;
  onPhotographer?: () => void;
  onQAReport?: () => void;
  onExit: () => void;
  showSettings?: boolean;
  showControls?: boolean;
  showNavigate?: boolean;
  isAssistantActive?: boolean;
}
export function InteriorTopBar({
  onReset,
  onSettings,
  onControls,
  onNavigate,
  onAssistant,
  onPhotographer,
  onQAReport,
  onExit,
  showSettings = false,
  showControls = false,
  showNavigate = false,
  isAssistantActive = false
}: InteriorTopBarProps) {
  const {
    isDark
  } = useTheme();
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  return <div className="absolute top-6 right-6 z-50 flex flex-col items-center" data-id="element-942">
      {/* Vertical Toolbar */}
      <div className={`flex flex-col items-center p-2 rounded-2xl backdrop-blur-xl border shadow-2xl transition-colors duration-300 ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/80 border-white/40'}`} data-id="element-943">
        {/* Reset Button */}
        <Tooltip content="Reset View" delay={0} position="left" data-id="element-944">
          <button onClick={onReset} className={`p-3 rounded-xl transition-all duration-200 group ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-black/5'}`} data-id="element-945">
            <RotateCcw className="w-5 h-5 transition-transform group-hover:-rotate-180 duration-500" data-id="element-946" />
          </button>
        </Tooltip>

        {/* Settings Button */}
        <Tooltip content="Settings" delay={0} position="left" data-id="element-947">
          <button onClick={onSettings} className={`p-3 rounded-xl transition-all duration-200 mt-1 ${showSettings ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-100 text-teal-600' : isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-black/5'}`} data-id="element-948">
            <Settings className={`w-5 h-5 transition-transform duration-500 ${showSettings ? 'rotate-90' : ''}`} data-id="element-949" />
          </button>
        </Tooltip>

        {/* Controls Button */}
        <Tooltip content="Controls" delay={0} position="left" data-id="element-950">
          <button onClick={onControls} className={`p-3 rounded-xl transition-all duration-200 mt-1 ${showControls ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-100 text-teal-600' : isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-black/5'}`} data-id="element-951">
            <Gamepad2 className="w-5 h-5" data-id="element-952" />
          </button>
        </Tooltip>

        {/* Navigate Button */}
        {onNavigate && <Tooltip content="How to Navigate" delay={0} position="left" data-id="element-953">
            <button onClick={onNavigate} className={`p-3 rounded-xl transition-all duration-200 mt-1 ${showNavigate ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-100 text-teal-600' : isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-black/5'}`} data-id="element-954">
              <Keyboard className="w-5 h-5" data-id="element-955" />
            </button>
          </Tooltip>}

        {/* AI Assistant Button */}
        {onAssistant && <Tooltip content="AI Assistant" delay={0} position="left" data-id="element-956">
            <button onClick={onAssistant} className={`p-3 rounded-xl transition-all duration-200 mt-1 ${isAssistantActive ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-100 text-teal-600' : isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-black/5'}`} data-id="element-957">
              <Sparkles className={`w-5 h-5 transition-transform duration-500 ${isAssistantActive ? 'rotate-12 scale-110' : ''}`} data-id="element-958" />
            </button>
          </Tooltip>}

        {/* QA Report Button (Builder Only) */}
        {onQAReport && <Tooltip content="Report Issue" delay={0} position="left" data-id="element-959">
            <button onClick={onQAReport} className={`p-3 rounded-xl transition-all duration-200 mt-1 ${isDark ? 'text-slate-400 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-500 hover:text-red-600 hover:bg-red-50'}`} data-id="element-960">
              <Bug className="w-5 h-5" data-id="element-961" />
            </button>
          </Tooltip>}

        {/* Divider */}
        <div className={`w-8 h-px my-2 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-962" />

        {/* Exit Button */}
        <div className="relative" data-id="element-963">
          <Tooltip content="Exit Model" delay={0} position="left" data-id="element-964">
            <button onClick={() => setShowExitConfirm(!showExitConfirm)} className={`p-3 rounded-xl transition-all duration-200 ${showExitConfirm ? 'bg-red-500/20 text-red-500' : isDark ? 'text-slate-400 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-500 hover:text-red-600 hover:bg-red-50'}`} data-id="element-965">
              <LogOut className="w-5 h-5" data-id="element-966" />
            </button>
          </Tooltip>

          {/* Exit Confirmation Popover */}
          <AnimatePresence data-id="element-967">
            {showExitConfirm && <motion.div initial={{
            opacity: 0,
            x: 10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            x: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            x: 10,
            scale: 0.95
          }} className={`absolute right-full top-0 mr-4 w-64 p-4 rounded-xl shadow-2xl backdrop-blur-xl border ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-968">
                <div className="flex items-start gap-3 mb-3" data-id="element-969">
                  <div className="p-2 rounded-full bg-red-500/10" data-id="element-970">
                    <AlertCircle className="w-5 h-5 text-red-500" data-id="element-971" />
                  </div>
                  <div data-id="element-972">
                    <h4 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-973">
                      Exit Model?
                    </h4>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-974">
                      Are you sure you want to leave the viewer?
                    </p>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-975">
                  <button onClick={() => setShowExitConfirm(false)} className={`flex-1 py-2 text-xs font-bold rounded-lg transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`} data-id="element-976">
                    Cancel
                  </button>
                  <button onClick={onExit} className="flex-1 py-2 text-xs font-bold rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors" data-id="element-977">
                    Exit
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}